import React, { useState, useEffect, useRef } from "react";
import {
  Paper, Typography, TextField, Button, Box, CircularProgress, Divider, IconButton, Dialog, DialogContent, Grid, MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../API/API";
import { getImageUrl } from "../utils/imageHelper";
import { toast } from "react-toastify";

const api = new API();
const MAX_IMAGES = 6;
const emptyForm = { phone_number: "", guest_name: "", room_number: "", notes: "" };

/**
 * Dummy (draft) guest entry: collect the customer's details / ID photos ahead of the stay.
 * Saved to guest-image-drafts only (never the real Saved Submissions); room is optional.
 */
export default function DummyEntry() {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyForm);
  const [images, setImages] = useState([]);              // new File objects
  const [existingImages, setExistingImages] = useState([]); // paths already on the draft (edit)
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSourceDialog, setOpenSourceDialog] = useState(false);
  const [openImage, setOpenImage] = useState(null);
  const [rooms, setRooms] = useState([]);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // All rooms (not just vacant) for the room dropdown
  useEffect(() => {
    api.get("api/rooms")
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
        const nums = [...new Set(list.map((r) => String(r.room_number || "").trim()).filter(Boolean))];
        nums.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        setRooms(nums);
      })
      .catch((err) => console.error("Error fetching rooms:", err));
  }, []);

  // Keep a room saved on the draft selectable even if it is no longer in the rooms list
  const roomOptions = formData.room_number && !rooms.includes(formData.room_number)
    ? [formData.room_number, ...rooms]
    : rooms;

  useEffect(() => {
    const item = location.state?.editDraft;
    if (item) {
      setEditId(item.id);
      setFormData({
        phone_number: item.phone_number ? String(item.phone_number) : "",
        guest_name: item.guest_name || "",
        room_number: item.room_number ? String(item.room_number) : "",
        notes: item.notes || "",
      });
      setExistingImages(Array.isArray(item.images) ? item.images : []);
      setImages([]);
    }
  }, [location.state]);

  const total = images.length + existingImages.length;
  const isValid = formData.phone_number.length === 10 && total > 0 && total <= MAX_IMAGES;

  const set = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_IMAGES - total;
    if (remaining <= 0) {
      toast.warning(`Maximum ${MAX_IMAGES} images allowed!`);
      return;
    }
    if (files.length > remaining) toast.warning(`Only ${remaining} image(s) added. Maximum is ${MAX_IMAGES}.`);
    setImages((p) => [...p, ...files.slice(0, remaining)]);
    e.target.value = "";
    setOpenSourceDialog(false);
  };

  const resetForm = () => {
    setEditId(null);
    setFormData(emptyForm);
    setImages([]);
    setExistingImages([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => data.append(k, (v || "").toString().trim()));
      images.forEach((img) => data.append("images[]", img));
      data.append("existing_images", JSON.stringify(existingImages));
      data.isMultipart = true;
      if (editId) {
        await api.post(`api/guest-image-drafts/${editId}`, data);
      } else {
        await api.post("api/guest-image-drafts", data);
      }
      toast.success(editId ? "Dummy submission updated!" : "Dummy submission saved!");
      resetForm();
      navigate("/dummy-submissions");
    } catch (err) {
      if (!err?.response?.data?.message) toast.error("Failed to save dummy submission.");
    } finally {
      setLoading(false);
    }
  };

  const field = (name, label, extra = {}) => (
    <TextField fullWidth size="small" margin="dense" label={label} name={name} value={formData[name]}
      onChange={(e) => set(name, e.target.value)} {...extra} />
  );

  return (
    <Box sx={{ width: "100%", mt: { xs: 0.5, md: 2 }, mb: 3, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: "#6a1b9a", fontSize: { xs: "1.1rem", md: "1.5rem" } }}>
          {editId ? "Edit Dummy Entry" : "Add Dummy Entry"}
        </Typography>
        <Button variant="outlined" size="small" startIcon={<FormatListBulletedIcon />} onClick={() => navigate("/dummy-submissions")}
          sx={{ color: "#6a1b9a", borderColor: "#6a1b9a", textTransform: "none", fontWeight: 600 }}>
          Dummy Submissions
        </Button>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid #d0d7de", borderRadius: 2, width: "100%", maxWidth: { xs: "100%", md: 550 } }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#24292f" }}>Customer Details (Dummy)</Typography>
          <Typography variant="caption" sx={{ color: "#6c757d" }}>
            Saved here only. Not shown in Saved Submissions / check-in until you Move it.
          </Typography>
          <Divider sx={{ my: 1.5 }} />

          <Box component="form" onSubmit={handleSubmit}>
            {field("phone_number", "Phone Number *", {
              value: formData.phone_number,
              onChange: (e) => { const v = e.target.value.replace(/\D/g, ""); if (v.length <= 10) set("phone_number", v); },
              inputProps: { maxLength: 10, inputMode: "numeric" },
              helperText: `${formData.phone_number.length}/10 digits`,
            })}
            {field("guest_name", "Guest Name")}
            {field("room_number", "Room Number (optional, can be set while moving)", {
              select: true,
              SelectProps: { MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } },
              children: [
                <MenuItem key="none" value=""><em>None</em></MenuItem>,
                ...roomOptions.map((rn) => <MenuItem key={rn} value={rn}>{rn}</MenuItem>),
              ],
            })}
            {field("notes", "Notes", { multiline: true, minRows: 2 })}

            <Button variant="outlined" fullWidth onClick={() => setOpenSourceDialog(true)}
              sx={{ mt: 2, py: 1.2, borderStyle: "dashed", textTransform: "none", borderColor: total ? "#6a1b9a" : "#ccc", color: total ? "#6a1b9a" : "text.secondary" }}>
              Select Guest Images ({total}/{MAX_IMAGES})
            </Button>
            <input type="file" hidden ref={cameraInputRef} accept="image/*" capture="environment" onChange={handleFileChange} />
            <input type="file" hidden multiple ref={galleryInputRef} accept="image/*" onChange={handleFileChange} />

            {total > 0 && (
              <Box sx={{ mt: 1.5, display: "flex", gap: 1, overflowX: "auto", py: 0.5, px: 0.5 }}>
                {existingImages.map((img, i) => (
                  <Box key={`ex-${i}`} sx={{ position: "relative", minWidth: 52 }}>
                    <Box component="img" src={getImageUrl(img)} onClick={() => setOpenImage(getImageUrl(img))}
                      sx={{ width: 52, height: 52, borderRadius: 1, objectFit: "cover", border: "1px solid #ddd", cursor: "pointer" }} />
                    <IconButton size="small" onClick={() => setExistingImages((p) => p.filter((_, j) => j !== i))}
                      sx={{ position: "absolute", top: -6, right: -6, bgcolor: "error.main", color: "white", width: 18, height: 18, p: 0, "&:hover": { bgcolor: "error.dark" } }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
                {images.map((file, i) => (
                  <Box key={`new-${i}`} sx={{ position: "relative", minWidth: 52 }}>
                    <Box component="img" src={URL.createObjectURL(file)} onClick={() => setOpenImage(URL.createObjectURL(file))}
                      sx={{ width: 52, height: 52, borderRadius: 1, objectFit: "cover", border: "1.5px solid #6a1b9a", cursor: "pointer" }} />
                    <IconButton size="small" onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                      sx={{ position: "absolute", top: -6, right: -6, bgcolor: "error.main", color: "white", width: 18, height: 18, p: 0, "&:hover": { bgcolor: "error.dark" } }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ mt: 1, textAlign: "center" }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: isValid ? "#2e7d32" : "#d32f2f" }}>
                {formData.phone_number.length < 10 ? "⚠️ Enter 10-digit phone number"
                  : total === 0 ? "⚠️ Upload at least 1 guest image (max 6)"
                  : "✓ Ready to save as dummy"}
              </Typography>
            </Box>

            <Button type="submit" variant="contained" fullWidth disabled={!isValid || loading}
              sx={{ mt: 1, py: 1.1, fontWeight: 700, textTransform: "none", bgcolor: "#6a1b9a", "&:hover": { bgcolor: "#4a148c" } }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : editId ? "Update Dummy" : "Save as Dummy"}
            </Button>
            {editId && (
              <Button fullWidth size="small" color="error" onClick={() => { resetForm(); navigate("/dummy-submissions"); }} sx={{ mt: 1, textTransform: "none" }}>
                Cancel Edit
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={openSourceDialog} onClose={() => setOpenSourceDialog(false)} PaperProps={{ sx: { borderRadius: 3, width: "90%", maxWidth: 300 } }}>
        <DialogContent>
          <Typography align="center" sx={{ fontWeight: 700, mb: 2 }}>Upload Image</Typography>
          <Grid container spacing={2}>
            {[["📷", "Camera", cameraInputRef], ["📁", "Gallery", galleryInputRef]].map(([icon, label, ref]) => (
              <Grid item xs={6} key={label}>
                <Box onClick={() => ref.current?.click()}
                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, p: 1.5, borderRadius: 2, bgcolor: "#f5f5f5", border: "1px solid #d0d7de", cursor: "pointer", "&:hover": { bgcolor: "#f3e5f5" } }}>
                  <Box sx={{ fontSize: "2rem" }}>{icon}</Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#6a1b9a" }}>{label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(openImage)} onClose={() => setOpenImage(null)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 1, display: "flex", justifyContent: "center" }}>
          {openImage && <Box component="img" src={openImage} sx={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
